<template>
  <div class="tutorial-container">
    <!-- 图片预览模态框 -->
    <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
      <div class="modal-content" @click.stop>
        <button class="close-btn" @click="closeImageModal">×</button>
        <img :src="selectedImage" :alt="selectedImageAlt" class="modal-image" />
        <div class="modal-caption">{{ selectedImageAlt }}</div>
      </div>
    </div>
    <!-- 教程导航 -->
    <div class="tutorial-nav">
      <button 
        v-for="(section, index) in sections" 
        :key="index"
        :class="['nav-item', { active: currentSection === index }]"
        @click="currentSection = index"
      >
        {{ section.title }}
      </button>
    </div>

    <!-- 教程内容 -->
    <div class="tutorial-content">
      <!-- 快速开始 -->
      <div v-if="currentSection === 0" class="tutorial-section">
        <h2 class="section-title">
          <RocketLaunchIcon class="title-icon" />
          快速开始
        </h2>
        <div class="step-list">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>上传图片</h3>
              <p>点击上传区域或拖拽图片到指定位置，支持 JPG、PNG、WebP 格式</p>
              <div class="step-tip">
                <LightBulbIcon class="tip-icon" />
                <span>建议使用清晰、高质量的图片以获得最佳效果</span>
              </div>
            </div>
          </div>
          
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>输入编辑指令</h3>
              <p>在文本框中详细描述您希望如何修改图片</p>
              <div class="step-tip">
                <LightBulbIcon class="tip-icon" />
                <span>描述越具体，结果越准确。例如："将背景改为日落时分"</span>
              </div>
            </div>
          </div>
          
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>开始生成</h3>
              <p>点击"开始生成"按钮，等待AI处理完成</p>
              <div class="step-tip">
                <LightBulbIcon class="tip-icon" />
                <span>处理时间通常在30-60秒，请耐心等待</span>
              </div>
            </div>
          </div>
          
          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-content">
              <h3>下载或继续编辑</h3>
              <p>生成完成后可以下载图片或将其作为新的输入继续编辑</p>
              <div class="step-tip">
                <LightBulbIcon class="tip-icon" />
                <span>可以多次迭代编辑，逐步完善您的创意</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 使用须知 -->
      <div v-if="currentSection === 1" class="tutorial-section">
        <h2 class="section-title">
          <ClipboardDocumentListIcon class="title-icon" />
          使用须知
        </h2>
        
        <div class="notice-grid">
          <div class="notice-card">
            <div class="notice-header">
              <ExclamationTriangleIcon class="notice-icon" />
              <h3>图像处理限制</h3>
            </div>
            <div class="notice-content">
              <ul>
                <li><strong>图像压缩：</strong>图像生成会将图片压缩至1300px最大宽度高度左右，如需进一步放大请使用图像放大功能</li>
                <li><strong>描述要求：</strong>请尽可能详细说明希望的处理效果，但模型理解能力有限，建议参考教程获取最佳效果</li>
                <li><strong>水印标识：</strong>根据相关部门备案要求，所有生成结果都会强制添加生成状态标识</li>
              </ul>
            </div>
          </div>

          <div class="notice-card">
            <div class="notice-header">
              <DocumentTextIcon class="notice-icon" />
              <h3>隐私保护</h3>
            </div>
            <div class="notice-content">
              <ul>
                <li><strong>图像存储：</strong>我们会存储您的图像以备安全性查阅（仅限违规违法时）</li>
                <li><strong>隐私承诺：</strong>不会以任何形式泄露、传播或再次索取您的图片</li>
                <li><strong>训练保护：</strong>不会将用户输入图像用于训练、制作训练集或公开传播</li>
                <li><strong>协议认可：</strong>上传图片即表示您认可我们的用户协议</li>
              </ul>
            </div>
          </div>

          <div class="notice-card">
            <div class="notice-header">
              <BoltIcon class="notice-icon" />
              <h3>适用场景</h3>
            </div>
            <div class="notice-content">
              <ul>
                <li><strong>基础操作：</strong>去除水印、去除人物、去除图像中的文字等</li>
                <li><strong>专业需求：</strong>针对cos圈、摄影圈等商单需求优化</li>
                <li><strong>技术优势：</strong>降低学习门槛，简化操作逻辑，快速掌握工具使用</li>
                <li><strong>效果保证：</strong>相比市面上其他工具，提供更稳定、更优质的处理效果</li>
              </ul>
            </div>
          </div>

          <div class="notice-card">
            <div class="notice-header">
              <LightBulbIcon class="notice-icon" />
              <h3>使用建议</h3>
            </div>
            <div class="notice-content">
              <ul>
                <li><strong>描述技巧：</strong>模型无法理解图像中的上下左右，需要明确告知内容主体</li>
                <li><strong>文字处理：</strong>对于文字标题，直接说明文字即可</li>
                <li><strong>局部修改：</strong>如需仅对部分进行修改，请详细描述具体位置和内容</li>
                <li><strong>参考教程：</strong>建议查看使用技巧和案例展示，学习最佳实践</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- 使用技巧 -->
      <div v-if="currentSection === 2" class="tutorial-section">
        <h2 class="section-title">
          <BoltIcon class="title-icon" />
          使用技巧
        </h2>
        
        <div class="tips-grid">
          <div class="tip-card">
            <div class="tip-header">
              <DocumentTextIcon class="tip-icon" />
              <h3>描述技巧</h3>
            </div>
            <div class="tip-content">
              <ul>
                <li><strong>具体明确：</strong>"将背景改为海边日落" 比 "改变背景" 更好</li>
                <li><strong>包含细节：</strong>描述颜色、风格、光线等具体元素</li>
                <li><strong>保持逻辑：</strong>确保描述的场景和元素相互协调</li>
                <li><strong>避免冲突：</strong>不要同时要求相互矛盾的修改</li>
                <li><strong>框体辅助：</strong>如果模型无法理解主体或者主体不明显，可以加入框体框住想要修改的主体，并在提示指令中带有“将框中的····”描述即可大幅提升修改成功率。</li>
              </ul>
            </div>
          </div>

          <div class="tip-card">
            <div class="tip-header">
              <PhotoIcon class="tip-icon" />
              <h3>图片选择</h3>
            </div>
            <div class="tip-content">
              <ul>
                <li><strong>分辨率：</strong>建议使用 1024x1024 或更高分辨率的图片</li>
                <li><strong>清晰度：</strong>选择清晰、无模糊的图片</li>
                <li><strong>构图：</strong>主体明确、背景简洁的图片效果更好</li>
                <li><strong>格式：</strong>支持 JPG、PNG、WebP 格式</li>
              </ul>
            </div>
          </div>

          <div class="tip-card">
            <div class="tip-header">
              <BoltIcon class="tip-icon" />
              <h3>最佳实践</h3>
            </div>
            <div class="tip-content">
              <ul>
                <li><strong>迭代编辑：</strong>分步骤进行复杂修改</li>
                <li><strong>保存中间结果：</strong>及时下载满意的版本</li>
                <li><strong>尝试不同描述：</strong>用不同表达方式描述相同需求</li>
                <li><strong>参考案例：</strong>查看下方案例获取灵感</li>
              </ul>
            </div>
          </div>

          <div class="tip-card">
            <div class="tip-header">
              <ExclamationTriangleIcon class="tip-icon" />
              <h3>注意事项</h3>
            </div>
            <div class="tip-content">
              <ul>
                <li><strong>版权：</strong>请确保您有权修改上传的图片</li>
                <li><strong>内容：</strong>避免生成不当或违规内容</li>
                <li><strong>精度：</strong>复杂场景可能需要多次尝试</li>
                <li><strong>耐心：</strong>高质量结果需要等待处理完成</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- 案例展示 -->
      <div v-if="currentSection === 3" class="tutorial-section">
        <h2 class="section-title">
          <SparklesIcon class="title-icon" />
          案例展示
        </h2>
        
        <div class="cases-grid">
          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/去除图像中人物的阴影.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/去除图像中人物的阴影.jpg', '去除人物阴影 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/去除图像中人物的阴影结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/去除图像中人物的阴影结果图.jpg', '去除人物阴影 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>去除人物阴影</h3>
              <p class="case-prompt"><strong>指令：</strong>"去除图像中人物的阴影"</p>
              <p class="case-description">AI智能识别并移除了图片中人物的阴影，使画面更加明亮清晰，同时保持了人物的自然姿态和整体画面的协调性。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/去除图像中的文字.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/去除图像中的文字.jpg', '去除图像文字 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/去除图像中的文字结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/去除图像中的文字结果图.jpg', '去除图像文字 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>去除图像文字</h3>
              <p class="case-prompt"><strong>指令：</strong>"去除图像中的文字"</p>
              <p class="case-description">AI精确识别并移除了图片中的文字内容，智能填充了背景，使修改后的图片看起来自然完整，仿佛文字从未存在过。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/去除图像红框中的人物.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/去除图像红框中的人物.jpg', '去除红框中人物 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/去除图像红框中的人物结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/去除图像红框中的人物结果图.jpg', '去除红框中人物 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>去除红框中人物</h3>
              <p class="case-prompt"><strong>指令：</strong>"去除图像红框中的人物"</p>
              <p class="case-description">AI完全移除了红框内的人物，并智能填充了背景内容，使画面看起来自然完整，保持了整体构图的平衡。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/去除水印.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/去除水印.jpg', '去除水印 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/去除水印结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/去除水印结果图.jpg', '去除水印 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>去除水印</h3>
              <p class="case-prompt"><strong>指令：</strong>"去除图片中的水印"</p>
              <p class="case-description">AI智能识别并移除了图片中的水印标识，同时保持背景的完整性和自然度，使图片看起来更加干净专业。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/去除红框中的文字.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/去除红框中的文字.jpg', '去除红框文字 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/去除红框中的文字结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/去除红框中的文字结果图.jpg', '去除红框文字 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>去除红框文字</h3>
              <p class="case-prompt"><strong>指令：</strong>"去除红框中的文字"</p>
              <p class="case-description">AI精确识别并移除了红框内的文字内容，智能填充了背景，使修改后的区域与周围环境完美融合。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/将人物头发修改为白色，但保留原始光照效果.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/将人物头发修改为白色，但保留原始光照效果.jpg', '修改头发颜色 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/将人物头发修改为白色，但保留原始光照效果结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/将人物头发修改为白色，但保留原始光照效果结果图.jpg', '修改头发颜色 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>修改头发颜色</h3>
              <p class="case-prompt"><strong>指令：</strong>"将人物头发修改为白色，但保留原始光照效果"</p>
              <p class="case-description">AI精确识别了头发区域，将颜色替换为白色，同时保持了原有的光照效果和质感，使颜色变化看起来自然真实。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/将衣服修改为白色.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/将衣服修改为白色.jpg', '修改衣服颜色 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/将衣服修改为白色结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/将衣服修改为白色结果图.jpg', '修改衣服颜色 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>修改衣服颜色</h3>
              <p class="case-prompt"><strong>指令：</strong>"将衣服修改为白色"</p>
              <p class="case-description">AI精确识别了衣服区域，将原有颜色替换为白色，保持了衣服的纹理、褶皱和光影效果，使颜色变化看起来自然真实。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img 
                  src="@/assets/用例/将图像变为彩色.jpg" 
                  alt="原始图片" 
                  @click="openImageModal('@/assets/用例/将图像变为彩色.jpg', '黑白转彩色 - 原始图片')"
                  class="clickable-image"
                />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img 
                  src="@/assets/用例/将图像变为彩色结果图.jpg" 
                  alt="编辑后" 
                  @click="openImageModal('@/assets/用例/将图像变为彩色结果图.jpg', '黑白转彩色 - 编辑后')"
                  class="clickable-image"
                />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>黑白转彩色</h3>
              <p class="case-prompt"><strong>指令：</strong>"将图像变为彩色"</p>
              <p class="case-description">AI智能地将黑白图像转换为彩色，为不同区域添加了合适的颜色，使画面更加生动鲜活，同时保持了原有的细节和质感。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img src="@/assets/用例/将光线更改为温暖的黄昏时分，但不要过于红，应该是偏黄.jpg" alt="原始图片" />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img src="@/assets/用例/将光线更改为温暖的黄昏时分，但不要过于红，应该是偏黄结果图.jpg" alt="编辑后" />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>调整光线效果</h3>
              <p class="case-prompt"><strong>指令：</strong>"将光线更改为温暖的黄昏时分，但不要过于红，应该是偏黄"</p>
              <p class="case-description">AI调整了整体光线效果，增加了温暖的橙黄色调，营造出黄昏时分的温馨氛围，同时保持了场景的细节和自然度。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img src="@/assets/用例/将夏天场景改为冬天，添加雪花.jpg" alt="原始图片" />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img src="@/assets/用例/将夏天场景改为冬天，添加雪花结果图.jpg" alt="编辑后" />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>季节变换</h3>
              <p class="case-prompt"><strong>指令：</strong>"将夏天场景改为冬天，添加雪花"</p>
              <p class="case-description">AI智能地将绿色植被转换为雪景，添加了飘落的雪花，调整了整体色调为冷色调，营造出冬季氛围。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img src="@/assets/用例/转为吉卜力风格.jpg" alt="原始图片" />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img src="@/assets/用例/转为吉卜力风格结果图.jpg" alt="编辑后" />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>吉卜力风格转换</h3>
              <p class="case-prompt"><strong>指令：</strong>"转为吉卜力风格"</p>
              <p class="case-description">AI保持了原始图片的构图和内容，同时应用了吉卜力动画的独特画风和色彩效果，创造出梦幻般的艺术效果。</p>
            </div>
          </div>

          <div class="case-item">
            <div class="case-images">
              <div class="case-before">
                <img src="@/assets/用例/转为水彩风格.jpg" alt="原始图片" />
                <span class="case-label">原始图片</span>
              </div>
              <div class="case-arrow">→</div>
              <div class="case-after">
                <img src="@/assets/用例/转为水彩风格结果图.jpg" alt="编辑后" />
                <span class="case-label">编辑后</span>
              </div>
            </div>
            <div class="case-info">
              <h3>水彩风格转换</h3>
              <p class="case-prompt"><strong>指令：</strong>"转为水彩风格"</p>
              <p class="case-description">AI保持了原始图片的构图和内容，同时应用了水彩画的笔触和色彩效果，创造出艺术感十足的视觉效果。</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 常见问题 -->
      <div v-if="currentSection === 4" class="tutorial-section">
        <h2 class="section-title">
          <QuestionMarkCircleIcon class="title-icon" />
          常见问题
        </h2>
        
        <div class="faq-list">
          <div class="faq-item">
            <div class="faq-question" @click="toggleFaq(0)">
              <span>处理时间需要多久？</span>
              <span class="faq-arrow" :class="{ 'expanded': expandedFaqs[0] }">▼</span>
            </div>
            <div class="faq-answer" :class="{ 'expanded': expandedFaqs[0] }">
              <p>通常需要30-60秒，具体时间取决于图片复杂度和服务器负载。如果遇到排队，可能需要等待更长时间。</p>
            </div>
          </div>

          <div class="faq-item">
            <div class="faq-question" @click="toggleFaq(1)">
              <span>为什么结果不如预期？</span>
              <span class="faq-arrow" :class="{ 'expanded': expandedFaqs[1] }">▼</span>
            </div>
            <div class="faq-answer" :class="{ 'expanded': expandedFaqs[1] }">
              <p>可能的原因：1) 描述不够具体，2) 图片质量较低，3) 需求过于复杂。建议尝试更详细的描述或分步骤编辑。</p>
            </div>
          </div>

          <div class="faq-item">
            <div class="faq-question" @click="toggleFaq(2)">
              <span>支持哪些图片格式？</span>
              <span class="faq-arrow" :class="{ 'expanded': expandedFaqs[2] }">▼</span>
            </div>
            <div class="faq-answer" :class="{ 'expanded': expandedFaqs[2] }">
              <p>支持 JPG、PNG、WebP 格式。建议使用清晰、高质量的图片以获得最佳效果。</p>
            </div>
          </div>

          <div class="faq-item">
            <div class="faq-question" @click="toggleFaq(3)">
              <span>可以多次编辑同一张图片吗？</span>
              <span class="faq-arrow" :class="{ 'expanded': expandedFaqs[3] }">▼</span>
            </div>
            <div class="faq-answer" :class="{ 'expanded': expandedFaqs[3] }">
              <p>可以！生成完成后，点击"重新编辑"按钮可以将结果作为新的输入继续编辑，实现复杂的多步骤修改。</p>
            </div>
          </div>

          <div class="faq-item">
            <div class="faq-question" @click="toggleFaq(4)">
              <span>如何获得最佳效果？</span>
              <span class="faq-arrow" :class="{ 'expanded': expandedFaqs[4] }">▼</span>
            </div>
            <div class="faq-answer" :class="{ 'expanded': expandedFaqs[4] }">
              <p>1) 使用高质量、清晰的图片 2) 提供具体、详细的描述 3) 分步骤进行复杂修改 4) 参考案例获取灵感</p>
            </div>
          </div>

          <div class="faq-item">
            <div class="faq-question" @click="toggleFaq(5)">
              <span>遇到错误怎么办？</span>
              <span class="faq-arrow" :class="{ 'expanded': expandedFaqs[5] }">▼</span>
            </div>
            <div class="faq-answer" :class="{ 'expanded': expandedFaqs[5] }">
              <p>如果遇到错误，请：1) 检查网络连接 2) 刷新页面重试 3) 尝试使用不同的图片或描述 4) 联系技术支持</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { 
  RocketLaunchIcon, 
  LightBulbIcon, 
  DocumentTextIcon, 
  PhotoIcon, 
  BoltIcon, 
  ExclamationTriangleIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon
} from '@heroicons/vue/24/outline';

const currentSection = ref(0);
const expandedFaqs = ref([false, false, false, false, false, false]);

// 图片预览相关
const showImageModal = ref(false);
const selectedImage = ref('');
const selectedImageAlt = ref('');

// webpack专用：批量导入 assets/用例 下所有图片
const images = {};
const req = require.context('@/assets/用例', false, /\.(png|jpe?g|webp)$/);
req.keys().forEach((key) => {
  // key 形如 './xxx.png'
  const fullPath = '@/assets/用例/' + key.replace('./', '');
  images[fullPath] = req(key);
});

function openImageModal(src, alt) {
  selectedImage.value = images[src];
  selectedImageAlt.value = alt;
  showImageModal.value = true;
}
function closeImageModal() {
  showImageModal.value = false;
}

const sections = [
  { title: '快速开始', icon: '🚀' },
  { title: '使用须知', icon: '📋' },
  { title: '使用技巧', icon: '🎯' },
  { title: '案例展示', icon: '✨' },
  { title: '常见问题', icon: '❓' }
];

const toggleFaq = (index) => {
  expandedFaqs.value[index] = !expandedFaqs.value[index];
};
</script>

<style scoped>
.tutorial-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.tutorial-nav {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.nav-item {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-item:hover {
  background: #e9ecef;
  color: #333;
}

.nav-item.active {
  background: #007AFF;
  color: white;
}

.tutorial-content {
  min-height: 600px;
}

.tutorial-section {
  animation: fadeIn 0.5s ease;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 2rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.title-icon {
  width: 2rem;
  height: 2rem;
  color: #007AFF;
}

/* 快速开始样式 */
.step-list {
  display: flex;
  flex-direction: column;
  gap: 3rem;
  max-width: 800px;
  margin: 0 auto;
}

.step-item {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  position: relative;
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 1.5rem;
  top: 4rem;
  width: 2px;
  height: calc(100% + 1rem);
  background: linear-gradient(180deg, #e2e8f0 0%, transparent 100%);
}

.step-number {
  width: 3rem;
  height: 3rem;
  background: #007AFF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.step-content {
  flex: 1;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.step-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.step-content p {
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.step-tip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f7fafc;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}

.tip-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #f59e0b;
  flex-shrink: 0;
}

/* 使用须知样式 */
.notice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
}

.notice-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.notice-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #007AFF 0%, #00C6FF 100%);
}

.notice-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 122, 255, 0.12);
  border-color: #e3f2fd;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.notice-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #007AFF;
  flex-shrink: 0;
}

.notice-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.notice-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.notice-content li {
  padding: 0.5rem 0;
  color: #4a5568;
  line-height: 1.6;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.95rem;
}

.notice-content li:last-child {
  border-bottom: none;
}

.notice-content strong {
  color: #1a202c;
  font-weight: 600;
}

/* 使用技巧样式 */
.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.tip-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.tip-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.tip-header .tip-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #007AFF;
  flex-shrink: 0;
}

.tip-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.tip-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tip-content li {
  padding: 0.5rem 0;
  color: #666;
  line-height: 1.6;
  border-bottom: 1px solid #e9ecef;
}

.tip-content li:last-child {
  border-bottom: none;
}

.tip-content strong {
  color: #333;
}

/* 案例展示样式 */
.cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(700px, 1fr));
  gap: 2rem;
}

.case-item {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.case-images {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  justify-content: center;
}

.case-before,
.case-after {
  flex: 1;
  text-align: center;
}

.case-before img,
.case-after img {
  width: 100%;
  max-width: 400px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.case-label {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.case-arrow {
  font-size: 1.5rem;
  color: #007AFF;
  font-weight: bold;
}

.case-info h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
}

.case-prompt {
  background: #e3f2fd;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.case-description {
  color: #666;
  line-height: 1.6;
}

/* 常见问题样式 */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  cursor: pointer;
  font-weight: 500;
  color: #333;
  transition: background-color 0.3s ease;
}

.faq-question:hover {
  background: #e9ecef;
}

.faq-arrow {
  transition: transform 0.3s ease;
  color: #007AFF;
}

.faq-arrow.expanded {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: white;
}

.faq-answer.expanded {
  max-height: 200px;
}

.faq-answer p {
  padding: 0 1.5rem 1.5rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* 放大图片模态框样式 */
.image-modal {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.modal-image {
  max-width: 80vw;
  max-height: 70vh;
  border-radius: 8px;
  background: #f8f9fa;
  margin-bottom: 1rem;
}
.close-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: transparent;
  border: none;
  font-size: 2rem;
  color: #888;
  cursor: pointer;
  z-index: 2;
}
.modal-caption {
  color: #333;
  font-size: 1rem;
  text-align: center;
}
.clickable-image {
  cursor: zoom-in;
  transition: box-shadow 0.2s;
}
.clickable-image:hover {
  box-shadow: 0 0 0 2px #007AFF;
}

/* 动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .tutorial-container {
    padding: 1rem;
  }
  
  .tutorial-nav {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .nav-item {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
  
  .step-list {
    gap: 2rem;
    padding: 0 1rem;
  }
  
  .step-item {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .step-item:not(:last-child)::after {
    display: none;
  }
  
  .step-number {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
    margin: 0 auto;
  }
  
  .step-content h3 {
    font-size: 1.25rem;
  }
  
  .step-content p {
    font-size: 0.9rem;
  }
  
  .step-tip {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    justify-content: center;
    width: fit-content;
    max-width: 100%;
  }
  
  .title-icon {
    width: 1.5rem;
    height: 1.5rem;
  }
  
  .tip-icon {
    width: 1rem;
    height: 1rem;
  }
  
  .tip-header .tip-icon {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  .cases-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .case-images {
    flex-direction: column;
    gap: 1rem;
  }
  
  .case-arrow {
    transform: rotate(90deg);
  }
  
  .case-before img,
  .case-after img {
    max-width: 100%;
    width: 100%;
  }
  
  .notice-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .notice-card {
    padding: 1.25rem;
  }
  
  .notice-header h3 {
    font-size: 1.1rem;
  }
  
  .notice-content li {
    font-size: 0.9rem;
  }
  
  .tips-grid {
    grid-template-columns: 1fr;
  }
}
</style> 